/**
 * Sidebar navigation model (E10-F01-T03).
 *
 * One entry per top-level nav-item. `adminOnly` gates the Users/Settings item:
 * the legacy `#nav-users` is hidden for every non-admin role (acceptance
 * criteria — "Только admin видит пункт Settings/Users"). Icons are Font Awesome
 * 6 class fragments (loaded from index.html, parity with the legacy CDN setup).
 */
import type { ViewId } from '../router/routes.js';
import type { AppRole } from '@eer/shared';

export interface NavItem {
  /** Stable DOM id (`nav-<id>`) — parity with legacy `#nav-users` etc. */
  readonly id: string;
  /** Target top-level view. */
  readonly view: ViewId;
  /** Visible label (hidden in the collapsed sidebar via `.sidebar-label`). */
  readonly label: string;
  /** Font Awesome 6 icon class (e.g. `fa-folder-open`). */
  readonly icon: string;
  /** When true, only `role === 'admin'` sees the item. */
  readonly adminOnly?: boolean;
  /**
   * When true the item can carry an unread-count badge (the Inbox). In the
   * collapsed sidebar the badge is absolutely positioned over the icon
   * (`.nav-badge` + the `.sidebar-collapsed` CSS rule) since the label — its
   * natural inline anchor — is hidden. The badge value itself is supplied at
   * render time by the feature owning the count (the Inbox), not stored here.
   */
  readonly badge?: boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
  // Legacy parity: the sidebar has a distinct 'Dashboard' item (fa-chart-line)
  // ABOVE 'Projects'. Both route to the same `projects` view (the legacy
  // `navigate('dashboard')` and `navigate('projects')` render the identical
  // project-list screen), so this is a second entry point, not a new view. The
  // active-state highlight keys on `view`, so both light up together — matching
  // the legacy markup where 'projects' is pre-highlighted on load.
  { id: 'dashboard', view: 'projects', label: 'Dashboard', icon: 'fa-chart-line' },
  { id: 'projects', view: 'projects', label: 'Projects', icon: 'fa-folder-open' },
  { id: 'inbox', view: 'inbox', label: 'Inbox', icon: 'fa-inbox', badge: true },
  // KS-02 — the contacts directory. Visible to every role: a recruiter needs to
  // find and correct a client contact, which is the whole point of the section.
  { id: 'clients', view: 'clients', label: 'Clients', icon: 'fa-address-book' },
  { id: 'research', view: 'research', label: 'Research', icon: 'fa-flask' },
  { id: 'users', view: 'users', label: 'Settings', icon: 'fa-gear', adminOnly: true },
];

/** Filter the nav by role — drops `adminOnly` items for non-admins. */
export function visibleNavItems(role: AppRole | string | undefined): readonly NavItem[] {
  return NAV_ITEMS.filter((item) => !item.adminOnly || role === 'admin');
}
